import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { LineOperatingCalendar, UpsertLineCalendarRequestInput } from "@opus-dt/shared-types";

// Hooks do calendário de operação da linha. GET público; PUT/DELETE GESTOR.
// Invalidação: a query do calendário + o detalhe da linha + os previews de cenário
// (que herdam o calendário da linha base — invalida `["scenarios"]` p/ refazer o preview leve).
// O expediente virou o TEMPO PLANEJADO do OEE, então salvar ou apagar o calendário passa
// a mudar o indicador na hora, e retroativamente para qualquer período consultado. Sem invalidar
// `["oee", lineId]` e `["dashboard"]`, o gestor cadastra o expediente e a Disponibilidade continua
// mostrando o número velho até o próximo refetch — parecendo que o cadastro não funcionou.

const calendarKey = (lineId: string) => ["lines", lineId, "calendar"] as const;

export function useLineCalendar(lineId: string) {
  return useQuery<LineOperatingCalendar | null>({
    queryKey: calendarKey(lineId),
    queryFn: () => api.get<LineOperatingCalendar | null>(`/api/v1/lines/${lineId}/calendar`),
    enabled: !!lineId,
  });
}

function useInvalidateCalendar(lineId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: calendarKey(lineId) });
    void qc.invalidateQueries({ queryKey: ["lines", lineId] });
    void qc.invalidateQueries({ queryKey: ["scenarios"] });
    void qc.invalidateQueries({ queryKey: ["oee", lineId] });
    void qc.invalidateQueries({ queryKey: ["dashboard"] });
    // Os indicadores da OP também saem de `calculate_oee` (`production-orders.indicators.ts`), então
    // são tão dependentes do expediente quanto o card da linha. Sem esta chave, salvar o calendário
    // atualiza a tela da linha e deixa o painel da OP ao lado exibindo o plano anterior.
    void qc.invalidateQueries({ queryKey: ["production-orders"] });
  };
}

export function useUpdateLineCalendar(lineId: string) {
  const invalidate = useInvalidateCalendar(lineId);
  return useMutation({
    mutationFn: (data: UpsertLineCalendarRequestInput) =>
      api.put<LineOperatingCalendar>(`/api/v1/lines/${lineId}/calendar`, data),
    onSuccess: invalidate,
  });
}

export function useDeleteLineCalendar(lineId: string) {
  const invalidate = useInvalidateCalendar(lineId);
  return useMutation({
    mutationFn: () => api.delete<void>(`/api/v1/lines/${lineId}/calendar`),
    onSuccess: invalidate,
  });
}
